import is from '@sindresorhus/is';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { getSortParams } from './common';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    globalSearchLearningOpportunityResponseSchema,
    globalSearchTermSchema,
} from '../../../data-contract/global-search';

import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type {
    GlobalSearchBySkillIdsParams,
    GlobalSearchLearningOpportunityResponse,
    GlobalSearchParams,
} from '../../../data-contract/global-search';
import type { UseQueryResult } from '@tanstack/react-query';

export const useGlobalSearchLearningOpportunitiesQuery = ({
    pagination,
    searchTerm,
    sortedFields,
}: GlobalSearchParams): UseQueryResult<GlobalSearchLearningOpportunityResponse> => {
    const params = new URLSearchParams({
        page: pagination.page.toString(),
        searchTerm,
        size: pagination.size.toString(),
    });

    if (is.array(sortedFields) && is.nonEmptyArray(sortedFields)) {
        params.append('sort', getSortParams(sortedFields));
    }

    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = '/global/search/learning-opportunities';

    const requestOptions = {
        searchParams: params.toString(),
    };

    return useQuery({
        enabled: globalSearchTermSchema.safeParse(searchTerm).success,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchLearningOpportunityResponseSchema, {
                pathname,
                requestOptions,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        queryKey: [pathname, 'GET', requestOptions],
    });
};

export const useGlobalSearchLearningOpportunitiesBySkillQuery = ({
    pagination,
    skillIds,
    sortedFields,
}: GlobalSearchBySkillIdsParams): UseQueryResult<GlobalSearchLearningOpportunityResponse> => {
    const params = new URLSearchParams({
        page: pagination.page.toString(),
        pageSize: pagination.size.toString(),
    });

    if (is.array(sortedFields) && is.nonEmptyArray(sortedFields)) {
        params.append('sort', getSortParams(sortedFields));
    }

    if (is.nonEmptyArray(skillIds)) {
        skillIds.forEach((skillId) => {
            params.append('skillIds', skillId);
        });
    }

    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = '/global/skill-id/learning-opportunities';

    const requestOptions = {
        searchParams: params.toString(),
    };

    return useQuery({
        enabled: is.nonEmptyArray(skillIds),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchLearningOpportunityResponseSchema, {
                pathname,
                requestOptions,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        queryKey: [pathname, 'GET', requestOptions],
    });
};
